import { ShieldCheck, ShieldQuestion, ShieldX } from 'lucide-react';
import type { CultivarRightsState } from '../../types/cultivar';
import { cn } from '../../utils/cn';

/**
 * Whether FactreeTech has checked an organisation's claim to the rights in a
 * protected cultivar (HHNodeServer cultivarRightsService). The server decides;
 * this only says it. Nothing is shown for a cultivar that claims no
 * protection, or when the server sent no state (an older backend).
 */

const BADGE: Record<Exclude<CultivarRightsState, 'not_required'>, { label: string; cls: string; Icon: typeof ShieldCheck }> = {
  verified: { label: 'Rights verified by FactreeTech', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: ShieldCheck },
  pending: { label: 'Rights not yet verified', cls: 'bg-amber-50 text-amber-800 border-amber-200', Icon: ShieldQuestion },
  rejected: { label: 'Rights not verified', cls: 'bg-red-50 text-red-800 border-red-200', Icon: ShieldX },
};

export function RightsBadge({ state, className }: { state?: CultivarRightsState | null; className?: string }) {
  if (!state || state === 'not_required') return null;
  const b = BADGE[state];
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium max-w-full', b.cls, className)}>
      <b.Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="break-words">{b.label}</span>
    </span>
  );
}

/**
 * The sentence a licensee reads before relying on a licence: who claims the
 * rights, and whether anyone has checked. `audience` changes the advice — the
 * issuer is told what its licensee will see and how to get it verified.
 */
export function RightsNotice({
  state, licensor, cultivar, audience = 'licensee', note, className,
}: {
  state?: CultivarRightsState | null;
  licensor?: string | null;
  cultivar?: string | null;
  audience?: 'licensee' | 'issuer' | 'owner';
  note?: string | null;
  className?: string;
}) {
  if (!state || state === 'not_required') return null;
  const who = licensor || 'the licensor';
  const what = cultivar || 'this cultivar';
  const b = BADGE[state];

  let text: string;
  if (audience === 'owner') {
    text = state === 'verified'
      ? `FactreeTech has checked your organisation's claim to the rights in ${what}. Licences on it say so.`
      : state === 'pending'
        ? `FactreeTech has not yet checked your organisation's claim to the rights in ${what}. You can still issue licences, and they show it as not yet verified. Changing the breeder or a registry number after it is verified sends it back for checking.`
        : `FactreeTech could not verify your organisation's claim to the rights in ${what}, so no new licences can be issued on it. Contact support to send evidence again.`;
  } else if (audience === 'issuer') {
    text = state === 'verified'
      ? `The licensee will see that FactreeTech has verified your rights in ${what}.`
      : state === 'pending'
        ? `The licensee will see that FactreeTech has not yet verified your rights in ${what}. You can still send it.`
        : `FactreeTech could not verify your rights in ${what}, so it cannot be licensed.`;
  } else {
    text = state === 'verified'
      ? `FactreeTech has checked that ${who} holds the rights to license ${what}. It is a check of the records shown to FactreeTech, not legal advice.`
      : state === 'pending'
        ? `FactreeTech has not yet checked that ${who} holds the rights to license ${what}. Before relying on this licence, ask ${who} for evidence: the registration certificate or their agreement with the breeder.`
        : `FactreeTech could not verify that ${who} holds the rights to license ${what}. Do not rely on this licence without checking with the breeder or the plant variety registry.`;
  }

  return (
    <div className={cn('flex items-start gap-2 rounded-lg border p-3 text-sm', b.cls, className)} role="note">
      <b.Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="min-w-0 space-y-1">
        <p className="font-semibold">{b.label}</p>
        <p className="break-words">{text}</p>
        {note && audience === 'owner' && (
          <p className="break-words"><span className="font-medium">Note from FactreeTech:</span> {note}</p>
        )}
      </div>
    </div>
  );
}

export default RightsNotice;
